// Batched, cached resolver for presigned image URLs.
//
// Gallery cards used to POST /uploads/get-secure-url one by one, once per photo.
// Requests made within a short window are coalesced into one
// POST /uploads/get-secure-urls call, and results are cached for a little under
// the presign lifetime so re-renders and reopened galleries do not refetch.
// Falls back to the single-key endpoint if the batch route is not available
// (older backend) so the gallery never goes blank.
import api from './api';

const BATCH_DELAY_MS = 40;
const MAX_BATCH = 50;
// Thumbnails may have to be generated server-side on a cold gallery; smaller
// batches keep each request inside the server's time budget.
const MAX_THUMBNAIL_BATCH = 24;
// Presigned URLs live 15 minutes on the backend; keep well inside that.
const CACHE_TTL_MS = 10 * 60 * 1000;

const cache = new Map(); // cacheKey -> { url, expiresAt }
const pending = new Map(); // batchKey -> { keys: Set, waiters: Map<key, [{resolve, reject}]>, timer }
let batchUnavailable = false;

const normaliseKey = (src) => String(src || '').replace(/\\/g, '/').replace(/^\/+/, '');
const cacheKeyFor = (key, variant, width) => `${variant}:${width || ''}:${key}`;

const fetchSingle = async (key) => {
  const response = await api.post('/uploads/get-secure-url', { path: key });
  return response.data?.url;
};

const flush = async (batchKey, batch) => {
  if (pending.get(batchKey) === batch) pending.delete(batchKey);
  const [variant, widthStr] = batchKey.split(':');
  const width = widthStr ? parseInt(widthStr, 10) : undefined;
  const keys = [...batch.keys];

  let urls = {};
  try {
    if (batchUnavailable) throw new Error('batch endpoint unavailable');
    const response = await api.post('/uploads/get-secure-urls', { paths: keys, variant, width });
    urls = response.data?.urls || {};
  } catch (err) {
    if (err?.response?.status === 404) batchUnavailable = true;
    // Fallback: one request per key through the legacy endpoint
    await Promise.all(keys.map(async (key) => {
      try {
        const url = await fetchSingle(key);
        if (url) urls[key] = url;
      } catch (singleErr) {
        console.error(`Failed to get secure URL for ${key}`, singleErr);
      }
    }));
  }

  const now = Date.now();
  keys.forEach((key) => {
    const url = urls[key];
    const waiters = batch.waiters.get(key) || [];
    if (url) {
      cache.set(cacheKeyFor(key, variant, width), { url, expiresAt: now + CACHE_TTL_MS });
      waiters.forEach(w => w.resolve(url));
    } else {
      waiters.forEach(w => w.reject(new Error(`No URL for ${key}`)));
    }
  });
};

/**
 * Resolve a presigned URL for an S3 key.
 * @param {string} src S3 key (with or without leading slash)
 * @param {{variant?: 'original'|'thumbnail', width?: number, force?: boolean}} options
 * @returns {Promise<string>}
 */
export const getSecureImageUrl = (src, { variant = 'original', width, force = false } = {}) => {
  const key = normaliseKey(src);
  if (!key) return Promise.reject(new Error('Missing image key'));
  const ck = cacheKeyFor(key, variant, width);
  const hit = cache.get(ck);
  if (!force && hit && hit.expiresAt > Date.now()) return Promise.resolve(hit.url);
  if (force) cache.delete(ck);

  const maxBatch = variant === 'thumbnail' ? MAX_THUMBNAIL_BATCH : MAX_BATCH;
  const batchKey = `${variant}:${width || ''}`;
  let batch = pending.get(batchKey);
  if (!batch || batch.keys.size >= maxBatch) {
    if (batch) { clearTimeout(batch.timer); flush(batchKey, batch); }
    const fresh = { keys: new Set(), waiters: new Map(), timer: null };
    fresh.timer = setTimeout(() => flush(batchKey, fresh), BATCH_DELAY_MS);
    pending.set(batchKey, fresh);
    batch = fresh;
  }
  batch.keys.add(key);
  return new Promise((resolve, reject) => {
    if (!batch.waiters.has(key)) batch.waiters.set(key, []);
    batch.waiters.get(key).push({ resolve, reject });
  });
};

export const clearSecureImageUrlCache = () => cache.clear();
